import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Design System Components
import Typography from '../Typography/Typography';

// Types
import { DialogProps, DialogSize } from '../../../types/DesignSystem/Dialog';
import { FOCUS_RING } from '../focusRing';
import { useDismissable } from '../useDismissable';
import { usePresence } from '../usePresence';
import { MOTION, exitDurationFor } from '../motion';
import { TONE_GLYPH } from '../toneGlyph';
/* § 232 — the set's Cross, not a private `CloseIcon` drawing: Toast's dismiss and
   Alert's dismiss already draw this exact glyph, so the system's three dismiss ×s
   are now ONE mark instead of two. */
import { Cross } from '../Iconography/Icons';
import { withClassName } from '../cn';
import { useMergedRef } from '../useMergedRef';

/**
 * Dialog Component
 *
 * A reusable modal dialog component for the design system.
 * Provides a centered overlay with header, content, and optional footer.
 *
 * Features:
 * - Backdrop overlay; click-to-close is OPT-IN via `closeOnOutsideClick` (default off)
 * - Header with title and close button
 * - Scrollable content area
 * - Optional footer for action buttons
 * - Size variants: sm (max 384px), md (max 490px), lg (max 1000px), xl (max
 *   1280px), half (50vw, floor 480px), or a `number` for an exact max-width in px
 *
 * Figma Reference: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11752-18875
 */
const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(function Dialog({
  open,
  isOpen: isOpenProp,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOutsideClick = false,
  className = '',
  headerActions,
  headerLeading,
  showCloseButton = true,
  tone,
  role = 'dialog',
  'aria-describedby': describedBy,
  initialFocusRef,
  'data-testid': dataTestId,
  'aria-labelledby': ariaLabelledByProp,
  'aria-label': ariaLabelProp,
  onClick: onClickProp,
  style: styleProp,
  ...rest
}, forwardedRef) {
  const panelRef = useRef<HTMLDivElement>(null);
  /* § 384 — the caller's ref reaches the panel too; `panelRef` stays the focus surface. */
  const setPanelRef = useMergedRef<HTMLDivElement>(panelRef, forwardedRef);
  /* Focus target region — see the note on `initialFocus` below. */
  const contentRef = useRef<HTMLDivElement>(null);
  /* § 410 — part of the layer: stays live while the page behind is inert. */
  const backdropRef = useRef<HTMLDivElement>(null);
  // § 331 — `open` is the canonical name; `isOpen` is the deprecated alias.
  const isOpen = open ?? isOpenProp ?? false;
  const { isPresent, phase } = usePresence(isOpen, exitDurationFor('dialog'));

  /*
    Scroll-aware chrome (§ 53) — the ADS-modal signature: the header's shadow
    appears once content has scrolled UNDER it, the footer's while there is more
    BELOW. The static § 35 keylines stay (they anchor the title band); these
    shadows ride on top and answer the one question a long dialog otherwise
    leaves open — "is there more?".
  */
  const [scrolledUnder, setScrolledUnder] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);
  const measureScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setScrolledUnder(el.scrollTop > 0);
    /* −1: browsers report fractional scroll positions; without the slack the
       footer shadow can flicker at the very bottom of a zoomed page. */
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);
  React.useLayoutEffect(() => {
    if (!isPresent) return undefined;
    measureScroll();
    /* Content can grow without a scroll event (async rows landing). jsdom has
       no ResizeObserver, hence the guard — the scroll handler still covers the
       interactive path there. */
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measureScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPresent, measureScroll]);

  /**
   * The dialog's accessible name is wired by `aria-labelledby`, so the id it points at
   * has to be unique per instance. It was the literal `"dialog-title"`, which meant two
   * mounted dialogs produced two elements with the same id and BOTH `aria-labelledby`
   * references resolved to whichever came first in the document — so the second dialog
   * announced the first one's title. Same defect as the hardcoded `clip0_3977_3515`
   * `clipPath` id that `InfoGlyph` replaced with `useId`.
   *
   * The literal survives as the title's `data-testid`, which is the right home for it:
   * a test hook may repeat, an `id` that ARIA dereferences may not.
   */
  const titleId = `${React.useId()}-dialog-title`;

  useDismissable({
    // `isPresent`, not `isOpen`: usePresence defers the mount by one render, so an
    // effect keyed on `isOpen` runs while the panel is still null — it then finds no
    // focusable surface and never re-runs, and focus never enters the dialog. Keying
    // on `isPresent` means the effect fires on the render that actually mounts it,
    // and the focus restore in its cleanup lands after the exit animation.
    isOpen: isPresent,
    onDismiss: onClose,
    modal: true,
    surface: panelRef,
    backdrop: backdropRef,
    /*
      Focus the CONTENT, never the header.

      The default — first focusable in the layer — is the close button here, because
      the header comes first in DOM order. Opened from the keyboard, `:focus-visible`
      matches and the dialog rendered a 2px `blue-900` ring around its own X: the
      loudest element in the dialog was Dismiss, and the first thing offered to a
      keyboard user was "leave". Verified with a probe render before changing
      anything — `document.activeElement` was `dlg-close`.

      Pointing at the content region means a form dialog lands on its first field
      (which is also where `:focus-visible` SHOULD fire, so the ring reads as
      intentional), and a dialog with no focusable content falls back to the panel
      rather than to Dismiss.

      § 347 — a caller can name the target instead (`initialFocusRef`): a destructive
      confirm has no field, and its right first stop is the LEAST destructive action.
    */
    initialFocus: initialFocusRef ?? contentRef,
    // …but stop listening for Escape the moment it starts closing, so a second
    // press during the exit does not call onClose again.
    closeOnEscape: isOpen,
    /*
      The backdrop's own onClick is unreachable (see the note above), so
      outside-dismiss runs through the hook's pointer-down path instead — and it
      stays OFF unless a caller opts in. `AttributeValuesModal`, the original
      consumer, holds unsaved edits and a stray click beside the panel should not
      discard them. Escape is different: a deliberate keypress routed to the same
      onClose as the visible close button.

      The opt-in exists because the PDW modals arrived from `ModalContainer`, where
      `clickOutSideClose` defaults to TRUE. Migrating them without it would quietly
      take a dismissal route away from users who already have it.

      Gated on `isOpen`, not `isPresent`: a mousedown during the exit animation must
      not call `onClose` a second time — the same reason `closeOnEscape` is.
    */
    isInside: (target) =>
      !!panelRef.current?.contains(target) ||
      /* A portalled Dropdown's listbox is not a DOM descendant of the panel. It
         marks itself with `[data-dropdown-portal]` for its own outside-click guard;
         reusing that marker here stops "open the menu" from closing the dialog
         under it. */
      !!(target as Element).closest?.('[data-dropdown-portal]'),
    closeOnOutsidePointerDown: closeOnOutsideClick && isOpen,
  });

  // Stay mounted through the exit animation, then unmount.
  if (!isPresent) {
    return null;
  }

  const exiting = phase === 'exiting';

  // Size variants matching Figma specifications.
  //
  // Every one of these MUST be a `max-w-*`, never a `w-*`. The panel below already
  // carries `tw-w-full` (so it shrinks on a narrow viewport), and `tw-w-full` and
  // `tw-w-[490px]` are both plain `width` declarations at equal specificity — the
  // winner is whichever Tailwind emits later, which is `tw-w-full`. `md` was
  // `tw-w-[490px]` until 2026-08-23 and therefore had no width at all: the default
  // dialog stretched to the full viewport (measured 1318px wide on a 1400px stage).
  // It survived unnoticed because the only consumer, AttributeValuesModal, passes
  // `size="lg"` — and `max-w-*` composes with `tw-w-full` instead of fighting it.
  const sizeClasses = {
    sm: 'tw-max-w-sm', // 384px
    md: 'tw-max-w-[490px]', // Figma spec: 490px
    lg: 'tw-max-w-[1000px]', // Large dialog
    /* § 345 — the step above `lg`. The name used to belong to `half` below, which is
       narrower than `lg` on every viewport under 2000px, so the ramp ran backwards. */
    xl: 'tw-max-w-[1280px]',
    /* Viewport-relative, with a floor — see `DialogSize`. The floor matters: below a
       960px viewport 50vw is narrower than 480px, and a two-column form does not fit
       in 400px. Same shape as SidePanel's `xl`. Called `xl` until § 345.
       § 376 — the floor is `min(480px, 100%)`: 480 wherever the container holds it (a
       viewport of 512px and up, identical to before), the full container below that. A
       bare 480px floor overflowed a 320px viewport on both sides (WCAG 1.4.10). */
    half: 'tw-max-w-[50vw] tw-min-w-[min(480px,100%)]',
  };
  // A numeric `size` sets max-width inline instead — see the `DialogSize` docblock
  // for why that's safer than a `className` override for an exact px value.
  const customWidth = typeof size === 'number' ? size : undefined;

  // Portal out to <body> so the dialog never inherits its parent's DOM
  // context. Callers like ExpandedRowContent render this inside a <tr>
  // (the row's expanded detail area), and a backdrop / dialog <div>
  // child of <tr> is invalid HTML — React logs validateDOMNesting
  // warnings and the browser silently reparents the nodes, which also
  // breaks any z-index or fixed-positioning the modal relies on. The
  // portal target is <body>, which makes the dialog a sibling of the
  // app root regardless of where in the React tree it's invoked. React
  // context still flows through the React tree, only the DOM tree
  // changes.
  return createPortal(
    <>
      {/* Backdrop */}
      <div data-testid={dataTestId ? `${dataTestId}-backdrop` : 'dialog-backdrop'}
        ref={backdropRef}
        className={`tw-fixed tw-inset-0 ${MOTION.overlay.scrim} tw-z-overlay ${
          exiting ? MOTION.overlay.exit : MOTION.overlay.enter
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog Container */}
      {/* `font-noto-sans`: this tree portals into document.body, whose stack leads
          with -apple-system — without re-stating the app face here, every dialog
          rendered SF Pro on macOS while the page behind it rendered Noto Sans. */}
      {/* § 394 — below 480px (`narrow:`, the overlays' viewport rung) the dialog trades its
          desktop margins for content: an 8px gutter instead of 16, the whole height instead of
          90vh, and a 16px inset instead of 24 on every band. Measured at 320×256 — 1280px at
          400% zoom — on a two-field form: the content band was 80px of a 230px dialog (the
          header's two-line title and the footer took the rest); it is 106px of 240 now. Nothing moves
          at 480px and wider. */}
      <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-overlay tw-p-4 narrow:tw-p-2 tw-font-noto-sans">
        <div
          /* § 384 — rest first (the Button.tsx rule): `id`, handlers and `data-*` a caller
             passes land on the panel; everything below is the component's. */
          {...rest}
          ref={setPanelRef}
          /* `outline-none`: in the no-focusable-content case the panel DOES take
             focus, and a UA ring around the whole surface is noise — the panel is
             not operable and is not a Tab stop, so SC 2.4.7 does not ask for an
             indicator on it. Every real control inside keeps its own ring. */
          className={withClassName(`tw-bg-surface-raised tw-outline-none tw-rounded-ovl tw-shadow-ds-lg tw-w-full ${customWidth === undefined ? sizeClasses[size as DialogSize] : ''} tw-max-h-[90vh] narrow:tw-max-h-full tw-flex tw-flex-col ${
            exiting ? MOTION.dialog.exit : MOTION.dialog.enter
          }`, className)}
          style={customWidth !== undefined ? { ...styleProp, maxWidth: `${customWidth}px` } : styleProp}
          onClick={(e) => {
            onClickProp?.(e);
            e.stopPropagation();
          }}
          role={role}
          aria-modal="true"
          aria-describedby={describedBy}
          /*
            No static `tabIndex` here — `useDismissable` adds `tabindex="-1"` only when
            the panel is actually the focus target, and removes it on close.

            A static one was wrong: a `tabindex="-1"` element is focusable BY CLICK,
            so clicking the dialog's padding landed focus on the panel, which drew the
            UA focus outline around the entire surface — visible in a screenshot.
          */
          /* § 384 — the title names the dialog unless the caller names it: their own
             `aria-labelledby` wins, and so does an `aria-label` given without one. */
          aria-labelledby={ariaLabelledByProp ?? (ariaLabelProp ? undefined : titleId)}
          aria-label={ariaLabelProp}
          data-testid={dataTestId}
        >
          {/*
            Header, WITH its bottom rule. It was removed on 2026-08-24 and put back the
            same day — see `okf/design-system/e2e.md` § 35.

            The argument for removing it was "three horizontal bands on a two-field
            form", counting the header rule, the footer rule and the footer's
            `grey-100` fill. That miscounted: the footer's rule and fill are ONE band,
            not two, so the dialog has two bands and always did.

            What removing it actually did was make the header and content padding
            additive — 16px + 20px of undifferentiated space with nothing in it — so
            the title stopped being anchored to anything and the gap read as a void
            rather than as separation. The rule is load-bearing: it closes the title
            band. It also means content that scrolls has something to scroll under.
          */}
          <div
            data-testid={dataTestId ? `${dataTestId}-header` : 'dialog-header'}
            /* `relative` guarantees the scroll shadow paints ABOVE the content
               that slides under it, whatever background that content brings. */
            className={`tw-relative tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 narrow:tw-px-4 narrow:tw-py-3 tw-border-b tw-border-edge tw-transition-shadow motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
              scrolledUnder ? 'tw-shadow-ds-sm' : ''
            }`}
          >
            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
              {headerLeading}
              {tone && (() => {
                const Glyph = TONE_GLYPH[tone];
                /* The tone's own TEXT ink — the same pair Toast/Status use
                   (danger 900; warning 950, not 900: the statusChip lesson),
                   so three components cannot disagree about a tone's colour. */
                return (
                  <Glyph
                    size={20}
                    aria-hidden="true"
                    className={`tw-shrink-0 ${tone === 'danger' ? 'tw-text-tone-danger-glyph' : 'tw-text-tone-warning-glyph'}`}
                    data-testid={dataTestId ? `${dataTestId}-tone-glyph` : 'dialog-tone-glyph'}
                  />
                );
              })()}
{/*
 * § 275 — this surface is a MOMENT, not a label, so it steps above body copy by SIZE.
 *
 * It was `title-header` (14/600) — the same token a table column header wears, and the
 * same one `Card` wears for a repeated object in a grid. Against `body-primary` (14/400)
 * beneath it, the only difference was weight, which is exactly the "hierarchy carried by
 * weight alone" § 243 added the middle heading tier to fix. `heading-3` (16/600) is that
 * tier, and it had TWO app uses in the nine months since.
 *
 * `Card` deliberately does NOT move: a card title is a repeated object's name, not a
 * moment, and 14/600 is the loudest it should be.
 *
 * § 345 — the STYLE is `heading-3`; the ELEMENT is `h2`. Left to its default,
 * `heading-3` renders an `<h4>`, and it sat inside a `<span>` that carried the id and
 * testid: a block heading in an inline box, and a fourth-level heading as the only
 * heading of a modal. A dialog is its own document to a screen reader, so its title is
 * the level under the page's `h1`. The id and testid move onto the heading itself.
 */}
              <Typography
                variant="heading-3"
                as="h2"
                id={titleId}
                data-testid={dataTestId ? `${dataTestId}-title` : 'dialog-title'}
                className="tw-text-ink"
              >
                {title}
              </Typography>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              {headerActions}
              {showCloseButton && (
              <button data-testid={dataTestId ? `${dataTestId}-close` : 'dialog-dialog-button-1'}
                type="button"
                onClick={onClose}
                /*
                  `rounded-full`, not `rounded-ctl`. The focus ring is drawn around
                  this element's own box, so a 6px radius on a 28px square made the
                  ring render as a rounded RECTANGLE — it read as a bordered box
                  rather than as a control, which is most of why the focused X looked
                  like a mistake even after § 32 stopped it being focused on open.

                  `p-1.5` takes the target from 28px to 32px (SC 2.5.8 wants 24), and
                  the hover now changes AREA, not just ink: `grey-600`->`grey-700` was
                  a 4.52->7.17 shift on a 20px glyph, which is almost no feedback.
                */
                className={`tw-p-1.5 tw-rounded-full tw-text-ink-tertiary hover:tw-bg-surface-hover-strong hover:tw-text-ink tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING}`}
                aria-label="Close dialog"
              >
                <Cross size={20} aria-hidden="true" />
              </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div
            ref={contentRef}
            onScroll={measureScroll}
            /* py-5, where the header and footer are py-4: the content should breathe
               slightly more than the chrome around it, and the removed header rule
               freed the space to do it with. */
            /* `text-ink` is the themed default for content that brings no ink of its
               own — without it, children inherit the page's un-themed ambient colour
               and go invisible in dark (§ 55). */
            /* `overscroll-contain` (§ 222): the wheel stops at the end of the dialog's
               own scroll — it must not chain to the page behind the scrim, which is
               inert to every OTHER input while the dialog is open. */
            className="tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-6 tw-py-5 narrow:tw-px-4 narrow:tw-py-4 tw-text-ink"
            data-testid={dataTestId ? `${dataTestId}-content` : 'dialog-content'}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            // The tint is what separates the action bar from the content above it:
            // when the content scrolls, it now visibly continues *under* a
            // different-coloured band instead of stopping dead against white.
            // `rounded-b-ovl` rather than `overflow-hidden` on the panel, so the
            // focus rings inside (box-shadows) are not clipped.
            // § 296 — `surface-recess`, not `surface-sunken`. This panel is `raised`,
            // and in dark `sunken` is the PAGE: the footer measured identical to the
            // backdrop behind the dialog (1.00:1) on a panel with no border, so the
            // bottom of every dialog dissolved into the scrim. The recess role is one
            // rung under its host in both themes; light is byte-identical.
            <div
              data-testid={dataTestId ? `${dataTestId}-footer` : 'dialog-footer'}
              className={`tw-relative tw-px-6 tw-py-4 narrow:tw-px-4 narrow:tw-py-3 tw-border-t tw-border-edge tw-bg-surface-recess tw-rounded-b-ovl tw-transition-shadow motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
                moreBelow ? 'tw-shadow-ds-sm-up' : ''
              }`}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
});

export default Dialog;
